<template>
  <div>
     <v-dialog v-model="invalidSuburbDialog" width="500">
      <v-card>
        <v-card-title
          class="headline grey lighten-2"
          primary-title>
          Invalid Address selected
        </v-card-title>
        <v-card-text>
          {{invalidSuburbDialogText}}
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="invalidSuburbDialog=false">Ok</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container fluid>
        <v-alert
          v-if="SHOW_ALERT"
          prominent
          type="error">
          <v-row align="center">
            <v-col class="grow">{{ ALERT_TEXT }}</v-col>
            <v-col class="shrink">
              <v-btn @click="accept()">Ok</v-btn>
            </v-col>
          </v-row>
        </v-alert>
        <v-row align="center" justify="center">
          <v-col lg="8" sm="12" md="10">
            <v-stepper non-linear v-model="currentStep">
              <v-stepper-header>
                <v-stepper-step
                  :editable="stepperEditable"
                  step="1"
                  :color="stepStatus(1)"
                  :complete="stepComplete(1)"
                  :rules="[() => !!transferDetailsValid]">
                  Transfer details
                  <small v-if="!transferDetailsValid">Information required</small>
                </v-stepper-step>

                <v-divider></v-divider>

                <v-stepper-step
                  step="2"
                  :editable="stepperEditable"
                  :color="stepStatus(2)"
                  :complete="stepComplete(2)"
                  :rules="[() => !!travellerDetailsValid]">
                  Traveller details
                  <small v-if="!travellerDetailsValid">Information required</small>
                </v-stepper-step>

                <v-divider></v-divider>

                <v-stepper-step
                  step="3"
                  :editable="stepperEditable"
                  :color="stepStatus(3)"
                  :complete="stepComplete(3)">
                  Transfer Options
                </v-stepper-step>

                <v-divider></v-divider>

                <v-stepper-step
                  step="4"
                  :editable="stepperEditable"
                  :color="stepStatus(4)"
                  :complete="stepComplete(4)">
                  Itinerary
                </v-stepper-step>

                <v-divider></v-divider>

                <v-stepper-step
                  step="5"
                  :color="stepStatus(5)"
                  :complete="stepComplete(5)">
                  Payment details
                </v-stepper-step>
              </v-stepper-header>

              <v-card dense class="mb-6" v-if="IS_ADMINISTRATOR || IS_AGENT">
                <v-card-title v-if="bookingNumber">
                  Editing Booking #: {{ bookingNumber }}
                </v-card-title>
                <CustomerSearch
                  :bus="bus"
                  :getFlightsOnSelection="true"
                  :canChangeCustomer="currentStep==1 && !bookingNumber"></CustomerSearch>
              </v-card>

              <v-stepper-content step="5">
                <ValidationObserver ref="payment" v-slot="{ invalid, valid, errors }">
                  <template v-if="!IS_BOOKING_PAID">
                    <v-form v-model="payment">
                      <v-card v-if="IS_ADMINISTRATOR || (IS_AGENT & PAYMENT_TYPES.length > 1)">
                        <v-list-item-subtitle>Payment types</v-list-item-subtitle>
                        <v-card-text class="pa-0">
                          <v-container fluid class="pa-0">
                            <ValidationProvider
                              rules="required"
                              v-slot="{ errors }"
                              name="Payment type">
                              <v-radio-group
                                :error-messages="errors"
                                v-model="paymentType">
                                  <v-radio
                                    v-for="paymenType in PAYMENT_TYPES"
                                    :key="paymenType.id"
                                    :label="paymenType.name"
                                    :value="paymenType.id"></v-radio>
                              </v-radio-group>
                            </ValidationProvider>
                          </v-container>
                        </v-card-text>
                      </v-card>
                      <v-card class="mb-6">
                        <v-list-item-subtitle>Payment details</v-list-item-subtitle>
                        <v-card-text class="pa-0">
                          <v-container fluid class="pa-0">
                            <v-row>
                              <v-col cols="6">
                                <div class="subtitle-1">Total fare</div>
                                <div class="headline">{{ fare | toCurrency }}</div>
                              </v-col>
                              <v-col cols="6" v-if="paymentType === 1">
                                <v-img
                                  :src="require('../assets/paymentMethods.jpg')"
                                  height="54"
                                  width="180"
                                ></v-img>
                              </v-col>
                            </v-row>
                            <template v-if="IS_ADMINISTRATOR && paymentType === 3">
                              <ValidationProvider
                                rules="required"
                                v-slot="{ errors }"
                                name="Account">
                                <v-select
                                    :error-messages="errors"
                                    :items="ACCOUNTS"
                                    item-text="name"
                                    item-value="id"
                                    label="Payment Account"
                                    outlined
                                    v-model="companyAccountId">
                                </v-select>
                              </ValidationProvider>
                            </template>
                            <template v-if="paymentType === 1">
                              <v-row>
                                <v-col cols="12" lg="6">
                                  <ValidationProvider
                                    rules="required"
                                    v-slot="{ errors }"
                                    name="Credit card number">
                                    <v-text-field
                                      :error-messages="errors"
                                      required
                                      v-model="creditCardNumber"
                                      label="Credit card number"
                                      placeholder="Enter credit card number"
                                      v-mask="creditCardMask"></v-text-field>
                                  </ValidationProvider>
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col cols="12" lg="6">
                                  <ValidationProvider
                                    rules="required"
                                    v-slot="{ errors }"
                                    name="Card expiry month">
                                    <v-select
                                      required
                                      :error-messages="errors"
                                      :items="expiryMonths"
                                      label="Expiry month"
                                      outlined
                                      v-model="expiryMonth"></v-select>
                                    </ValidationProvider>
                                </v-col>
                                <v-col cols="12" lg="6">
                                  <ValidationProvider
                                    rules="required"
                                    v-slot="{ errors }"
                                    name="Card expiry year">
                                    <v-select
                                      required
                                      :error-messages="errors"
                                      :items="expiryYears"
                                      label="Expiry year"
                                      outlined
                                      v-model="expiryYear"></v-select>
                                  </ValidationProvider>
                                </v-col>
                              </v-row>
                            </template>
                          </v-container>
                        </v-card-text>
                      </v-card>
                    </v-form>
                  </template>
                  <template v-if="IS_BOOKING_PAID && !LOADING">
                    <v-alert type="warning">
                      This booking has been paid for by Credit Card.
                      Payment reference #: {{booking.paymentReferenceNumber}}.
                    </v-alert>
                    <v-card class="mb-6">
                        <v-list-item-subtitle>Payment details</v-list-item-subtitle>
                        <v-card-text class="pa-0">
                          <v-container fluid class="pa-0">
                            <v-row>
                              <v-col cols="6">
                                <div class="subtitle-1">Total fare</div>
                                <div class="headline">{{ fare | toCurrency }}</div>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-card-text>
                    </v-card>
                  </template>
                  <v-row>
                    <v-col>
                      <v-btn
                        color="primary"
                        class="float-left"
                        @click="stepChange(4, true)">
                        Previous
                      </v-btn>
                      <v-btn color="primary" class="float-right" @click="confirmBooking()">
                        Confirm booking
                      </v-btn>
                    </v-col>
                  </v-row>
                </ValidationObserver>
              </v-stepper-content>

              <v-stepper-content step="4" class="px-2">
                <v-card class="mb-6 px-0">
                  <v-card-text class="px-0 title">
                    <v-row>
                      <v-col class="text-right py-0">Total fare</v-col>
                      <v-col class="text-left py-0">{{ fare | toCurrency }}</v-col>
                    </v-row>
                    <v-container fluid class="px-0">
                      <v-row>
                        <v-col cols="12">
                          <ValidationObserver
                            ref="itineraryDetails"
                            v-slot="{ invalid, valid, errors }">
                            <v-form v-model="itineraryDetailsValid">
                              <itinerary></itinerary>
                            </v-form>
                          </ValidationObserver>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>
                </v-card>
                <v-btn color="primary" class="float-left" @click="stepChange(3, true)">
                  Previous
                </v-btn>
                <v-btn
                color="primary"
                class="float-right"
                @click="stepChange(5, true)">Next</v-btn>
              </v-stepper-content>

              <v-stepper-content step="3">
                <ValidationObserver ref="travellerOptions" v-slot="{ invalid, valid, errors }">
                  <v-form v-model="travellerOptionsValid">
                    <booking-charges
                      v-if="IS_ADMINISTRATOR"></booking-charges>
                    <travel-options></travel-options>
                    <v-card class="mb-6">
                      <v-list-item-subtitle>Additional info</v-list-item-subtitle>
                      <v-card-text>
                        <v-textarea
                          class="mx-2"
                          label="To airport"
                          rows="1"
                          prepend-icon="mdi-comment-text-multiple-outline"
                          v-model="additionalInfoToAirport"
                          v-if="direction === 1 || direction === 3"
                          maxlength="500"
                          counter
                        ></v-textarea>
                        <v-textarea
                          class="mx-2"
                          label="From airport"
                          rows="1"
                          prepend-icon="mdi-comment-text-multiple-outline"
                          v-model="additionalInfoFromAirport"
                          v-if="direction === 2 || direction === 3"
                          maxlength="500"
                          counter
                        ></v-textarea>
                      </v-card-text>
                    </v-card>
                    <v-btn color="primary" class="float-left" @click="stepChange(2, true)">
                      Previous
                    </v-btn>
                    <v-btn color="primary" class="float-right" @click="stepChange(4, true)">
                      Review and Confirm
                    </v-btn>

                  </v-form>
                </ValidationObserver>
              </v-stepper-content>

              <v-stepper-content step="2">
                <ValidationObserver ref="travellerDetails" v-slot="{ invalid, valid, errors }">
                  <v-form v-model="travellerDetailsValid">
                    <traveller-details :bookingId="id" :customerId="customerId"></traveller-details>
                    <v-btn color="primary" class="float-left" @click="stepChange(1, true)">
                      Previous
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="float-right"
                      @click="stepChange(3, true)">Next</v-btn>
                  </v-form>
                </ValidationObserver>
              </v-stepper-content>

              <v-stepper-content step="1">
                <ValidationObserver ref="transferDetails" v-slot="{ invalid, valid, errors }">
                  <v-form v-model="transferDetailsValid">
                    <v-card class="mb-6">
                      <v-list-item-subtitle>Transit details</v-list-item-subtitle>
                      <v-card-text class="px-0">
                        <ValidationProvider rules="required" v-slot="{ errors }">
                          <v-chip-group
                            v-model="direction"
                            active-class="green white--text"
                            column
                            @change="onDirectionChanged(true)">
                            <v-chip :value="1">
                              <v-avatar left>
                                <v-icon>mdi-airplane-takeoff</v-icon>
                              </v-avatar>To Airport
                            </v-chip>
                            <v-chip :value="2">
                              <v-avatar left>
                                <v-icon>mdi-airplane-landing</v-icon>
                              </v-avatar>From Airport
                            </v-chip>
                            <v-chip :value="3">
                              <v-avatar left>
                                <v-icon>mdi-sync</v-icon>
                              </v-avatar>Return trip
                            </v-chip>
                          </v-chip-group>
                        </ValidationProvider>
                        <v-radio-group
                          v-model="exclusiveCharter"
                          v-on:change="exclusiveCharterChanged()">
                          <v-radio :value="false" :disabled="SUBURB_EXCLUSIVE_ONLY">
                            <template v-slot:label>
                              <div>
                                <span>Shared transfer:</span>
                                <div class="text-caption">
                                  Pickup/drop-off multiple passengers
                                </div>
                              </div>
                            </template>
                          </v-radio>
                          <v-radio :value="true">
                            <template v-slot:label>
                              <div>
                                <span>Private charter:</span>
                                <div class="text-caption">
                                  Exclusively for you and your travelling companions
                                </div>
                              </div>
                            </template>
                          </v-radio>
                        </v-radio-group>
                        <ValidatedSelect
                          name="Passengers"
                          rules="required|min_value:1"
                          v-model="passengers"
                          :items="passengersItems"
                          :labelValue="'No. of passengers (including children)'"
                          v-on:input="CHECK_AND_CALCULATE_FARE()"
                          required>
                        </ValidatedSelect>
                      </v-card-text>
                    </v-card>

                    <pickup-dropoff :bus="bus"></pickup-dropoff>

                    <div v-if="IS_ADMINISTRATOR">
                      <ValidatedInput
                          name="discountDollars"
                          rules="min_value:0"
                          v-model="discountDollars"
                          :labelValue="'Discount ($)'"
                          :placeholderValue="'Enter discount amount'"
                          :type="'number'"
                          >
                      </ValidatedInput>
                      <ValidatedInput
                          name="discountPercent"
                          v-model="discountPercent"
                          :labelValue="'Discount (%)'"
                          :placeholderValue="'Enter discount percentage'"
                          :type="'number'"
                          >
                      </ValidatedInput>
                    </div>
                    <v-btn
                      v-if="false"
                      color="secondary"
                      class="float-left"
                      @click="calculateFare()">
                      Calculate fare
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="float-right"
                      @click="stepChange(2, true)"
                      >Next</v-btn>
                  </v-form>
                </ValidationObserver>
              </v-stepper-content>

            </v-stepper>
          </v-col>
        </v-row>
    </v-container>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { mask } from 'vue-the-mask';
import { mapFields } from 'vuex-map-fields';
/* eslint-disable camelcase */
import { required, min_value } from 'vee-validate/dist/rules';
/* eslint-enable camelcase */

import
{
  extend,
  ValidationProvider,
  ValidationObserver,
  setInteractionMode,
} from 'vee-validate';

import store from '../store';
import ValidatedSelect from '@/components/ValidatedSelect.vue';
import ValidatedInput from '@/components/ValidatedInput.vue';
import TravelOptions from '@/components/TravelOptions.vue';
import TravellerDetails from '@/components/TravellerDetails.vue';
import Itinerary from '@/components/Itinerary.vue';
import PickupDropoff from '@/components/PickupDropOff.vue';
import CustomerSearch from '@/components/CustomerSearch.vue';
import storeConstants from '@/store/storeConstants';
import BookingCharges from '@/components/BookingCharges.vue';

setInteractionMode('eager');

extend('required', required);
extend('min_value', min_value);

export default {
  name: 'booking',
  directives: {
    mask,
  },
  components: {
    'travel-options': TravelOptions,
    'traveller-details': TravellerDetails,
    itinerary: Itinerary,
    'pickup-dropoff': PickupDropoff,
    ValidationProvider,
    ValidationObserver,
    ValidatedSelect,
    ValidatedInput,
    CustomerSearch,
    'booking-charges': BookingCharges,
  },
  props: {
    id: {
      type: Number,
      default: undefined,
    },
    customerId: {
      type: Number,
      default: undefined,
    },
  },
  data: () => ({
    bus: new Vue(),
    currentObserver: null,
    transferDetailsValid: false,
    travellerDetailsValid: false,
    travellerOptionsValid: false,
    itineraryDetailsValid: false,
    payment: false,
    passengersItems: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
    stepperEditable: false,
    sticky: true,
    elavation: 3,
    invalidSuburbDialog: false,
    invalidSuburbDialogText: '',
    searchText: 'default',
    currentStep: 1,
    date: new Date().toISOString().substr(0, 10),
    menu2: false,
    menu1: false,
    limitToCountries: ['AU'],
    items: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    creditCardMask: '####-####-####-####',
    expiryMonths: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    // TODO Move expiry years to config.
    expiryYears: [2024, 2025, 2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033, 2034, 2035],
  }),
  created() {
    if (this.id && (this.IS_ADMINISTRATOR || this.IS_AGENT)) {
      this.$store.commit(storeConstants.SET_LOADING_TEXT, `Retrieving Booking: ${this.id} ...`);
      this.$store.commit(storeConstants.SET_LOADING, true);

      this.GET_BOOKING(this.id)
        .then(() => {
          // do not re-calc fare on-load.
          this.onDirectionChanged(false);

          this.bus.$emit(storeConstants.EVT_GET_BOOKING_COMPLETE, {
            data: this.BOOKING,
          });
          this.$store.commit(storeConstants.SET_LOADING, false);
        }, (error) => {
          this.invalidSuburbDialog = true;
          this.invalidSuburbDialogText = error;
          this.$store.commit(storeConstants.SET_LOADING, false);
        })
        .catch(() => {
          this.$store.commit(storeConstants.SET_LOADING, false);
        });
    } else if (this.customerId && (this.IS_ADMINISTRATOR || this.IS_AGENT)) {
      this.$store.commit(storeConstants.SET_LOADING_TEXT, `Setting up Booking for customer: ${this.customerId} ...`);
      this.$store.commit(storeConstants.SET_LOADING, true);
      this.GET_CUSTOMER(this.customerId)
        .then((data) => {
          this.bus.$emit(storeConstants.EVT_NEW_CUSTOMER_BOOKING, {
            data,
          });
          this.$store.commit(storeConstants.SET_LOADING, false);
        }, (error) => {
          this.invalidSuburbDialog = true;
          this.invalidSuburbDialogText = error;
          this.$store.commit(storeConstants.SET_LOADING, false);
        })
        .catch(() => {
          this.$store.commit(storeConstants.SET_LOADING, false);
        });
    }
  },
  mounted() {
    // set the 1st step validation observer
    this.currentObserver = this.$refs.transferDetails;

    if (this.IS_ADMINISTRATOR) {
      this.GET_PAYMENT_TYPES();
      this.GET_ACCOUNTS();
      this.GET_ALL_PICKUP_TIMES();
    } else if (this.IS_AGENT) {
      this.GET_PAYMENT_TYPES();
    }

    if (this.IS_AUTHENTICATED && this.IS_CUSTOMER_ONLY) {
      this.emailAddress = this.CURRENT_USER_EMAIL;
      this.firstName = this.CURRENT_USER_FIRST_NAME;
      this.lastName = this.CURRENT_USER_LAST_NAME;
    }
    this.GET_BOOKING_DATES();
  },
  beforeRouteEnter(to, from, next) {
    // resetting the booking in store.
    store.dispatch(storeConstants.RESET_BOOKING);
    next();
  },
  beforeRouteLeave(to, from, next) {
    this.SET_ALERT_DETAILS({ alertText: null, showAlert: false });
    next();
  },
  methods: {
    ...mapActions([storeConstants.SAVE_BOOKING,
      storeConstants.CALCULATE_FARE,
      storeConstants.CHECK_AND_CALCULATE_FARE,
      storeConstants.GET_FLIGHTS,
      storeConstants.GET_ALL_PICKUP_TIMES,
      storeConstants.SET_ALERT_DETAILS,
      storeConstants.GET_PAYMENT_TYPES,
      storeConstants.GET_ACCOUNTS,
      storeConstants.GET_BOOKING,
      storeConstants.GET_CUSTOMER,
      storeConstants.GET_BOOKING_DATES]),
    exclusiveCharterChanged() {
      this.bus.$emit(storeConstants.EVT_EXCLUSIVE_CHARTER_CHANGED);
    },
    accept() {
      this.SET_ALERT_DETAILS({ alertText: undefined, showAlert: false });
    },
    calculateFare() {
      this.$store.commit(storeConstants.SET_LOADING_TEXT, 'Calculating fare...');
      this.$store.commit(storeConstants.SET_LOADING, true);

      this.CALCULATE_FARE(this.booking)
        .then(() => {
          this.$store.commit(storeConstants.SET_LOADING, false);
        }, (error) => {
          this.invalidSuburbDialog = true;
          this.invalidSuburbDialogText = error;
          this.$store.commit(storeConstants.SET_LOADING, false);
        })
        .catch(() => {
          this.$store.commit(storeConstants.SET_LOADING, false);
        });
    },
    onDirectionChanged(recalculateFare) {
      if (this.suburb) {
        const retrieveFlights = (tempDirection) => {
          const payload = { direction: tempDirection, suburb: this.suburb };
          this.GET_FLIGHTS(payload)
            .then((flights) => {
              if (tempDirection === 1) {
                this.$store.commit(storeConstants.SET_DEPARTING_FLIGHTS, flights);
              } else {
                this.$store.commit(storeConstants.SET_ARRIVING_FLIGHTS, flights);
              }
            });
        };

        if (this.direction === 3) {
          retrieveFlights(1);
          retrieveFlights(2);
        } else {
          retrieveFlights(this.direction);
        }
        if (recalculateFare) {
          this.CHECK_AND_CALCULATE_FARE();
        }
      }
    },
    stepComplete(step) {
      return this.currentStep > step;
    },
    stepStatus(step) {
      return this.currentStep > step ? 'green accent-4' : 'red accent-4';
    },
    stepChange(step, isButtonNav) {
      // no validation if going backwards.
      const previousClicked = step < this.currentStep;
      // TODO: validate the current validation object.
      this.currentObserver.validate().then((success) => {
        if (!isButtonNav && !success) {
          return;
        }

        if (success || previousClicked) {
          this.currentStep = Number(step);

          switch (step) {
            case 1:
              this.currentObserver = this.$refs.transferDetails;
              if (!previousClicked) this.transferDetailsValid = success;
              break;
            case 2:
              this.currentObserver = this.$refs.travellerDetails;
              if (!previousClicked) this.travellerDetailsValid = success;
              break;
            case 3:
              this.currentObserver = this.$refs.travellerOptions;
              if (!previousClicked) this.travellerOptionsValid = success;
              break;
            case 4:
              this.currentObserver = this.$refs.itineraryDetails;
              break;
            case 5:
              if (!previousClicked) this.itineraryDetailsValid = success;
              break;
            default:
              break;
          }

          if (this.currentStep === 4) {
            this.$store.commit(storeConstants.SET_LOADING_TEXT, 'Calculating fare...');
            this.$store.commit(storeConstants.SET_LOADING, true);

            this.CALCULATE_FARE(this.booking)
              .then(() => {
                this.$store.commit(storeConstants.SET_LOADING, false);
                this.currentStep = 4;
              }, (error) => {
                this.invalidSuburbDialog = true;
                this.invalidSuburbDialogText = error;
                this.currentStep = 3; // set back to previous step.
                this.$store.commit(storeConstants.SET_LOADING, false);
              })
              .catch(() => {
                this.currentStep = 3;
                this.$store.commit(storeConstants.SET_LOADING, false);
              });
          }
        }
      });
    },
    confirmBooking() {
      this.$refs.payment.validate().then((success) => {
        if (!success) return;

        this.$store.commit(storeConstants.SET_LOADING_TEXT, 'Processing payment details...');
        this.$store.commit(storeConstants.SET_LOADING, true);

        this.SAVE_BOOKING(this.booking)
          .then(() => {
            if (!this.booking.isPaymentSuccessful) {
              this.SET_ALERT_DETAILS({
                alertText: this.booking.paymentDescriptionText,
                showAlert: true,
              });
              this.$store.commit(storeConstants.SET_LOADING, false);
              return;
            }
            this.$router.push({ name: 'confirmation' });
          },
          (error) => {
            if (error.response?.data?.errors) {
              const keyNames = Object.keys(error.response.data.errors);
              const errors = keyNames.map(a => error.response.data.errors[a].join());
              this.SET_ALERT_DETAILS({ alertText: errors.join(' : '), showAlert: true });
            } else if (error.response.data.error) {
              this.SET_ALERT_DETAILS({ alertText: error.response.data.error, showAlert: true });
            }
            this.$store.commit(storeConstants.SET_LOADING, false);
          })
          .catch(() => {
            this.$store.commit(storeConstants.SET_LOADING, false);
          });
      });
    },
  },
  computed: {
    ...mapGetters([storeConstants.BOOKING,
      storeConstants.CONFIGURATION,
      storeConstants.ALERT_TEXT,
      storeConstants.SHOW_ALERT,
      storeConstants.IS_ADMINISTRATOR,
      storeConstants.IS_AGENT,
      storeConstants.IS_CUSTOMER_ONLY,
      storeConstants.PAYMENT_TYPES,
      storeConstants.ACCOUNTS,
      storeConstants.SUBURB_EXCLUSIVE_ONLY,
      storeConstants.IS_AUTHENTICATED,
      storeConstants.CURRENT_USER_EMAIL,
      storeConstants.CURRENT_USER_FIRST_NAME,
      storeConstants.CURRENT_USER_LAST_NAME,
      storeConstants.IS_BOOKING_PAID,
      storeConstants.LOADING]),
    // The `mapFields` function takes an array of
    // field names and generates corresponding
    // computed properties with getter and setter
    // functions for accessing the Vuex store.
    ...mapFields([
      'booking',
      'booking.pickUpDetails.pickupTime',
      'booking.companyAccountId',
      'booking.fare',
      'booking.paymentType',
      'booking.bookingNumber',
      'booking.direction',
      'booking.paymentReferenceNumber',
      'booking.passengers',
      'booking.exclusiveCharter',
      'booking.firstName',
      'booking.lastName',
      'booking.mobileNumber',
      'booking.phoneNumber',
      'booking.emailAddress',
      'booking.discountDollars',
      'booking.discountPercent',
      'booking.paymentDetails.creditCardNumber',
      'booking.paymentDetails.expiryMonth',
      'booking.paymentDetails.expiryYear',
      'booking.selectedAddress.subPremise', // TODO: check if these are required.
      'booking.selectedAddress.streetNumber',
      'booking.selectedAddress.street',
      'booking.selectedAddress.suburb',
      'booking.selectedAddress.state',
      'booking.selectedAddress.country',
      'booking.selectedAddress.postCode',
      'booking.selectedAddress.latitude',
      'booking.selectedAddress.longitude',
      'booking.selectedAddress.formattedAddress', // TODO: check if these are required.
      'booking.additionalInfoFromAirport',
      'booking.additionalInfoToAirport',
    ]),
    ...mapFields({
      pickUpDetailsTravelDate: 'booking.pickUpDetails.travelDate',
      pickUpDetailsDepartingFlight: 'booking.pickUpDetails.departingFlight',
      pickUpDetailsPickupTime: 'booking.pickUpDetails.pickupTime',
      dropOffDetailsTravelDate: 'booking.dropOffDetails.travelDate',
      dropOffDetailsArrivingFlight: 'booking.dropOffDetails.arrivingFlight',
    }),
  },
};
</script>
<style scoped>
@media only screen and (max-width: 959px) {
  .v-stepper:not(.v-stepper--vertical) .v-stepper__label {
    display: inline;
  }
}
.v-button-toggle{
  margin-top: 0.5rem;
}
tbody.itinerary_details > tr > td:first-child {
  font-weight: 700;
}
</style>
<style>
.v-list-item__subtitle {
    font-weight: 700;
}
</style>
